import React, { useState } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import PremioAPI from '../premio-api';

const SEGMENT_LABELS = {
  FINALISTA: 'Finalistas',
  MENCION: 'Menciones honoríficas (no seleccionadas)',
  NO_SELECCIONADO: 'No seleccionadas',
  MENSAJE_ESPECIAL: 'Mensajes especiales',
};

const STATUS_LABELS = {
  DRAFT: 'Borrador',
  SUBMITTED: 'Enviada',
  UNDER_REVIEW: 'En revisión',
  REVIEWED: 'Revisada',
  PRESELECTED: 'Preseleccionada',
  FINALIST: 'Finalista',
  AWARDED: 'Premiada',
  REJECTED: 'No seleccionada',
};
const statusLabel = (status) => STATUS_LABELS[status] || status || 'Sin postulación asociada';

const errorMessage = (error) => {
  const detail = error?.data?.detail || error?.data?.message;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const message = detail.map((item) => item.msg || item.message).filter(Boolean).join('. ');
    if (message) return message;
  }
  return error?.message || 'No fue posible procesar el archivo. Inténtalo nuevamente.';
};

export default function FeedbackImport({ convocatoriaId, title, disabled, onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [validatedFile, setValidatedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(false);
  const rows = result?.rows || [];
  const errors = result?.errors || [];
  const canApply = result?.dry_run === true && result.ready > 0 && !errors.length && validatedFile === file && !applied;

  const openDialog = () => {
    setFile(null);
    setValidatedFile(null);
    setResult(null);
    setError('');
    setApplied(false);
    setOpen(true);
  };

  const selectFile = (event) => {
    const selected = event.target.files?.[0] || null;
    setResult(null);
    setValidatedFile(null);
    setApplied(false);
    setError('');
    if (selected && (!/\.csv$/i.test(selected.name) || selected.size > 1024 * 1024)) {
      setFile(null);
      setError('Selecciona un archivo CSV de hasta 1 MB.');
      return;
    }
    setFile(selected);
  };

  const processFile = async (dryRun) => {
    if (!file || busy || (!dryRun && !canApply)) return;
    setBusy(true);
    setError('');
    if (dryRun) {
      setResult(null);
      setValidatedFile(null);
    }
    try {
      const response = await PremioAPI.importApplicationFeedback(convocatoriaId, file, dryRun);
      const data = response?.data;
      const validRows = Array.isArray(data?.rows) && data.rows.every((row) => (
        row && ['FINALIST', 'REJECTED'].includes(row.status_after)
        && typeof row.decision_message === 'string' && typeof row.honorable_mention === 'boolean'
      ));
      if (!data || data.dry_run !== dryRun || !Array.isArray(data.errors) || !validRows
          || typeof data.total !== 'number' || typeof data.matched_applications !== 'number' || typeof data.ready !== 'number'
          || (!dryRun && typeof data.updated !== 'number')) {
        throw new Error('El servidor no entregó un resultado válido. Vuelve a validar el archivo.');
      }
      setResult(data);
      if (dryRun) {
        setValidatedFile(data.errors.length ? null : file);
      } else if (!data.errors.length) {
        setApplied(true);
        onImported();
      }
    } catch (requestError) {
      setValidatedFile(null);
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="button secondary convocatoria-postulantes__download-statuses" onClick={openDialog} disabled={disabled}>
        Cargar resultados y feedback
      </button>
      <Dialog open={open} onClose={() => { if (!busy) setOpen(false); }} maxWidth="md" fullWidth aria-labelledby="feedback-import-title" disableEscapeKeyDown={busy}>
        <DialogTitle id="feedback-import-title">Cargar resultados y feedback — {title}</DialogTitle>
        <DialogContent dividers>
          <p>Valida el CSV antes de publicar los resultados y el feedback de cada postulación de Emerge.</p>
          <p>Se publicarán el mensaje de resultado y el comentario adicional disponible. El estado cambiará a Finalista o No seleccionada según el resultado del CSV. Las menciones honoríficas quedan como No seleccionada, con su reconocimiento. No se publican puntajes, posiciones ni rankings.</p>
          <p>Usa el CSV original, separado por punto y coma y en UTF-8, con sus columnas EMAIL, NOMBRE, EMPRENDIMIENTO, CATEGORIA, N_POSTULACION, SEGMENTO, PUNTAJE, CORTE, PERTINENCIA, TRAYECTORIA, EVIDENCIA, ORIGINALIDAD, VINCULO y FEEDBACK.</p>
          <label htmlFor="feedback-import-file">Archivo CSV (máximo 1 MB)</label>
          <input id="feedback-import-file" type="file" accept=".csv,text/csv" onChange={selectFile} disabled={busy || applied} />
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <>
              <Alert severity={applied ? 'success' : errors.length ? 'error' : 'info'}>
                {applied
                  ? `Resultados y feedback publicados. Postulaciones actualizadas: ${result.updated}.`
                  : `${result.total} filas, ${result.matched_applications} postulaciones: ${result.ready} resultados listos para publicar y ${errors.length} errores.`}
              </Alert>
              <ul>
                {Object.entries(result.segments || {}).map(([segment, count]) => <li key={segment}>{SEGMENT_LABELS[segment] || segment}: {count}</li>)}
                <li>Con comentario adicional: {rows.filter((row) => row.has_feedback).length}. Sin comentario adicional: {rows.filter((row) => !row.has_feedback).length}. Todas recibirán su mensaje de resultado.</li>
                <li>Con feedback previo que se reemplazará: {rows.filter((row) => row.will_replace).length}.</li>
              </ul>
              {!!errors.length && (
                <TableContainer>
                  <Table size="small" aria-label="Errores del CSV">
                    <TableHead><TableRow><TableCell>Fila</TableCell><TableCell>Correo</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
                    <TableBody>{errors.map((item, index) => <TableRow key={`${item.row}-${index}`}><TableCell>{item.row}</TableCell><TableCell>{item.email || '—'}</TableCell><TableCell>{item.message}</TableCell></TableRow>)}</TableBody>
                  </Table>
                </TableContainer>
              )}
              {!!rows.length && (
                <TableContainer style={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader aria-label="Vista previa de resultados y feedback">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fila</TableCell><TableCell>Correo</TableCell><TableCell>Segmento</TableCell><TableCell>Categoría</TableCell>
                        <TableCell>Estado actual</TableCell><TableCell>Nuevo estado</TableCell><TableCell>Mensaje de resultado</TableCell>
                        <TableCell>Comentario adicional</TableCell><TableCell>Reemplaza</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{rows.map((row) => (
                      <TableRow key={`${row.row}-${row.email}`}>
                        <TableCell>{row.row}</TableCell>
                        <TableCell>
                          {row.email}
                          {Array.isArray(row.application_ids) && row.application_ids.length > 1 && (
                            <small style={{ display: 'block' }}>{row.application_ids.length} postulaciones</small>
                          )}
                        </TableCell>
                        <TableCell>{SEGMENT_LABELS[row.segment] || row.segment}</TableCell><TableCell>{row.category || '—'}</TableCell>
                        <TableCell>{statusLabel(row.status_before)}</TableCell>
                        <TableCell>{statusLabel(row.status_after)}{row.honorable_mention && <p>Mención honorífica</p>}</TableCell>
                        <TableCell style={{ whiteSpace: 'pre-wrap', maxWidth: 350, minWidth: 200 }}>{row.decision_message}</TableCell>
                        <TableCell style={{ whiteSpace: 'pre-wrap', maxWidth: 350, minWidth: 200 }}>{row.feedback || 'Sin comentario adicional'}</TableCell>
                        <TableCell>{row.will_replace ? 'Sí' : 'No'}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <button type="button" className="button secondary" disabled={busy} onClick={() => setOpen(false)}>Cerrar</button>
          {!applied && <button type="button" className="button secondary" disabled={!file || busy} onClick={() => processFile(true)}>{busy ? 'Procesando…' : 'Validar archivo'}</button>}
          {!applied && <button type="button" className="button primary" disabled={!canApply || busy} onClick={() => processFile(false)}>Publicar resultados y feedback</button>}
        </DialogActions>
      </Dialog>
    </>
  );
}
